/**
 * DOM Utilities
 *
 * Helper functions for common DOM operations.
 */

/**
 * Query selector shorthand
 * @param {string} selector - CSS selector
 * @param {Element} context - Context element (default: document)
 * @returns {Element|null} The found element or null
 */
export function $(selector, context = document) {
    return context.querySelector(selector);
}

/**
 * Query selector all shorthand
 * @param {string} selector - CSS selector
 * @param {Element} context - Context element (default: document)
 * @returns {Element[]} Array of found elements
 */
export function $$(selector, context = document) {
    return Array.from(context.querySelectorAll(selector));
}

/**
 * Add event listener with delegation support
 *
 * @param {Element|string} target - Element or selector for delegation
 * @param {string} event - Event name
 * @param {string|Function} selectorOrHandler - Delegate selector or handler
 * @param {Function} handler - Handler function (if using delegation)
 * @returns {Function} Cleanup function to remove the listener
 */
export function on(target, event, selectorOrHandler, handler) {
    const element = typeof target === 'string' ? $(target) : target;

    if (!element) {
        console.warn(`Element not found: ${target}`);
        return () => {};
    }

    // If selectorOrHandler is a function, it's a direct handler
    if (typeof selectorOrHandler === 'function') {
        element.addEventListener(event, selectorOrHandler);
        return () => element.removeEventListener(event, selectorOrHandler);
    }

    // Otherwise, it's event delegation
    const delegatedHandler = (e) => {
        const delegateTarget = e.target.closest(selectorOrHandler);
        if (delegateTarget && element.contains(delegateTarget)) {
            handler.call(delegateTarget, e, delegateTarget);
        }
    };

    element.addEventListener(event, delegatedHandler);
    return () => element.removeEventListener(event, delegatedHandler);
}

/**
 * Run callback when DOM is ready
 * @param {Function} callback - Callback to run
 */
export function ready(callback) {
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', callback);
    } else {
        callback();
    }
}

/**
 * Get data attributes from an element as an object
 * @param {Element} element - The element
 * @returns {Object} Data attributes object
 */
export function getData(element) {
    const data = {};
    for (const key in element.dataset) {
        // Convert kebab-case to camelCase is automatic with dataset
        data[key] = element.dataset[key];
    }
    return data;
}

/**
 * Set multiple data attributes on an element
 * @param {Element} element - The element
 * @param {Object} data - Data to set
 */
export function setData(element, data) {
    for (const key in data) {
        element.dataset[key] = data[key];
    }
}

/**
 * Create an element with attributes and children
 *
 * @param {string} tag - Tag name
 * @param {Object} attrs - Attributes to set
 * @param {Array|string} children - Child elements or text content
 * @returns {Element} The created element
 */
export function createElement(tag, attrs = {}, children = []) {
    const element = document.createElement(tag);

    for (const key in attrs) {
        if (key === 'class' || key === 'className') {
            element.className = attrs[key];
        } else if (key === 'style' && typeof attrs[key] === 'object') {
            Object.assign(element.style, attrs[key]);
        } else if (key.startsWith('data')) {
            const dataKey = key.replace(/^data/, '').replace(/^./, c => c.toLowerCase());
            element.dataset[dataKey] = attrs[key];
        } else if (key.startsWith('on') && typeof attrs[key] === 'function') {
            const eventName = key.slice(2).toLowerCase();
            element.addEventListener(eventName, attrs[key]);
        } else {
            element.setAttribute(key, attrs[key]);
        }
    }

    if (typeof children === 'string') {
        element.textContent = children;
    } else if (Array.isArray(children)) {
        children.forEach(child => {
            if (typeof child === 'string') {
                element.appendChild(document.createTextNode(child));
            } else if (child instanceof Element) {
                element.appendChild(child);
            }
        });
    }

    return element;
}

/**
 * Toggle class on element
 * @param {Element} element - The element
 * @param {string} className - Class name to toggle
 * @param {boolean} force - Force add (true) or remove (false)
 * @returns {boolean} Whether class is now present
 */
export function toggleClass(element, className, force) {
    return element.classList.toggle(className, force);
}

/**
 * Show an element (remove hidden class)
 * @param {Element} element - The element
 */
export function show(element) {
    element.classList.remove('hidden');
    element.removeAttribute('hidden');
}

/**
 * Hide an element (add hidden class)
 * @param {Element} element - The element
 */
export function hide(element) {
    element.classList.add('hidden');
}

/**
 * Check if element is visible
 * @param {Element} element - The element
 * @returns {boolean} Whether element is visible
 */
export function isVisible(element) {
    return !element.classList.contains('hidden') && !element.hasAttribute('hidden');
}

/**
 * Serialize form data to object
 * @param {HTMLFormElement} form - The form element
 * @returns {Object} Form data as object
 */
export function serializeForm(form) {
    const formData = new FormData(form);
    const data = {};

    for (const [key, value] of formData.entries()) {
        // Handle array inputs (name="foo[]")
        if (key.endsWith('[]')) {
            const arrayKey = key.slice(0, -2);
            if (!data[arrayKey]) {
                data[arrayKey] = [];
            }
            data[arrayKey].push(value);
        } else {
            data[key] = value;
        }
    }

    return data;
}

export default {
    $,
    $$,
    on,
    ready,
    getData,
    setData,
    createElement,
    toggleClass,
    show,
    hide,
    isVisible,
    serializeForm
};
